import { Component, Injector, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Address } from '../../../models/address-model';
import { Occupancy } from '../../../models/enums/occupancy.enum';
import { ResidencyType } from '../../../models/enums/residency-type.enum';
import { ResidencyAddress } from '../../../models/residency-address.model';
import { SubjectProperty } from '../../../models/subject-property.model';
import { AddressComponent } from '../../address/address.component';
import {
  applyRefiSameAsCurrentAddressDefaults, clearRefiSameAsCurrentAddressDefaults, isCompleteAddress,
  isPresentAddressTheRefiProperty,
} from 'projects/borrower-app/src/app/wizard/refi-current-address.util';
import { WizardStepComponentBase } from '../wizard-step-base.component';

@Component({
    selector: 'current-address',
    templateUrl: 'current-address.component.html',
    standalone: false
})
export class CurrentAddressComponent extends WizardStepComponentBase<any> {

  @ViewChild(AddressComponent) addressComponent: AddressComponent | undefined;

  @ViewChild('addressForm')
  addressForm: NgForm | undefined;

  address: | Address | undefined = new Address();

  refiPropertySameAsCurrentAddress: boolean | undefined = undefined;

  isRefinance: boolean = false;

  private readonly _presentAddress: ResidencyAddress;

  constructor(private readonly _injector: Injector) {
    super(_injector);

    if (!this.mortgageApplication.subjectProperty) {
      this.mortgageApplication.subjectProperty = new SubjectProperty();
    }

    this.saveMortgageApplicationBeforeNextStep = true;
    const presentAddress = this.currentBorrower.residencyAddresses.find(a => a.residencyType == ResidencyType.PresentAddress);

    this.isRefinance = this.mortgageApplication.subjectProperty.purposeOfLoan === "Refinance";

    this.refiPropertySameAsCurrentAddress = this.wizardFlowService.context.refiAddressSameAsCurrentAddress;
    // LLS-638: the answer is only kept in memory, so on resume re-derive it from the saved addresses:
    // Yes when the present address is the refinance property, No when it is a different one. Both
    // must be complete to compare; otherwise the question stays unanswered.
    if (this.isRefinance && this.refiPropertySameAsCurrentAddress === undefined
      && isCompleteAddress(presentAddress?.address) && isCompleteAddress(this.mortgageApplication.subjectProperty)) {
      this.refiPropertySameAsCurrentAddress = isPresentAddressTheRefiProperty(presentAddress, this.mortgageApplication.subjectProperty);
    }

    const occupancy = this.mortgageApplication.subjectProperty.propertyWillBe;
    if (this.isRefinance && (occupancy === Occupancy.VacationHome || occupancy === Occupancy.InvestmentProperty)) {
      this.refiPropertySameAsCurrentAddress = false;
    }

    if (presentAddress) {
      this._presentAddress = presentAddress;
    } else {
      const residencyAddress = new ResidencyAddress(ResidencyType.PresentAddress);
      residencyAddress.residencyAddressId = null;
      this.currentBorrower.residencyAddresses.push(residencyAddress);
      this._presentAddress = residencyAddress;
    }
    this.address = this._presentAddress.address;
  }

  onAddressQuestionAnswered = () => {
    if (this.refiPropertySameAsCurrentAddress) {
      if (this.address) {
        this.address.address1 = this.mortgageApplication.subjectProperty.address1;
        this.address.city = this.mortgageApplication.subjectProperty.city;
        this.address.state = this.mortgageApplication.subjectProperty.state;
        this.address.zipCode = this.mortgageApplication.subjectProperty.zipCode;
        this.address.county = this.mortgageApplication.subjectProperty.county;
      }
    } else {
      // LLS-638: a No after an earlier Yes must not keep the Own default. Runs before clearAddress()
      // because it only clears an Own that still belongs to the refinance property.
      clearRefiSameAsCurrentAddressDefaults(this._presentAddress, this.mortgageApplication.subjectProperty);
      this.clearAddress();
    }
  }

  onNextClicked() {
    if (this.isRefinance) {
      this.wizardFlowService.context.refiAddressSameAsCurrentAddress = this.refiPropertySameAsCurrentAddress;
    }
    if (this.addressForm) {
      this.addressForm.form.markAllAsTouched();
      if (this.addressComponent?.validate() && this.addressForm.form.valid) {
        if (this.address) {
          if (this.isRefinance && this.refiPropertySameAsCurrentAddress) {
            this.mortgageApplication.subjectProperty.address1 = this.address.address1;
            this.mortgageApplication.subjectProperty.city = this.address.city;
            this.mortgageApplication.subjectProperty.state = this.address.state;
            this.mortgageApplication.subjectProperty.zipCode = this.address.zipCode;
            this.mortgageApplication.subjectProperty.county = this.address.county;
            // LLS-638: default Ownership Status = Own here so it holds even when the flow has no
            // own/rent step. Selling = No follows from the REO card's 'Retained' default.
            applyRefiSameAsCurrentAddressDefaults(this._presentAddress);
          }
          let mailingAddress = this.currentBorrower.residencyAddresses.find(a => a.residencyType == ResidencyType.MailingAddress);
          if (!mailingAddress) {
            mailingAddress = new ResidencyAddress(ResidencyType.MailingAddress);
            this.currentBorrower.residencyAddresses.push(mailingAddress);
          }
          mailingAddress.address.address1 = this.address.address1;
          mailingAddress.address.city = this.address.city;
          mailingAddress.address.state = this.address.state;
          mailingAddress.address.zipCode = this.address.zipCode;
          mailingAddress.address.county = this.address.county;
        }
        super.onNextClicked();
      }
    }
  }

  private clearAddress = () => {
    if (this.address) {
      this.address.address1 = "";
      this.address.city = "";
      this.address.state = "";
      this.address.zipCode = "";
    }
  }
}
